import { useCallback, useEffect, useState } from "react";

import { API_PHASE2_GET_FORM } from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import { ApiError, getApi } from "../../../../utils/api.ts";
import {
  parsePostJoinFormListResponse,
  type OnboardInvitationRow,
} from "../schemas/onboard-invitation-row.schema.ts";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { SQE_PHASE2, sqeRows } from "../../onboarding/sqe/sqe-onboarding-api.ts";

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

/** Loads the full post-join form list (no pagination; API returns a JSON array). */
export function usePostJoinFormList() {
  const isSqe = useSqeLabs();
  const [rows, setRows] = useState<OnboardInvitationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getApi<unknown>(isSqe ? `${SQE_PHASE2}/pending-forms` : API_PHASE2_GET_FORM, accessToken(), "json", signal);
      setRows(isSqe ? sqeRows(data).rows.map((row) => ({
        ...row,
        name: String(row.employeeName ?? row.name ?? ""),
        email: String(row.email ?? row.ccEmail ?? ""),
        createdOn: String(row.createdOn ?? row.createdAt ?? ""),
        status: String(row.status ?? "PENDING"),
      })) : parsePostJoinFormListResponse(data));
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to load post-join forms.";
      setError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [isSqe]);

  useEffect(() => {
    const ac = new AbortController();
    void refetch(ac.signal);
    return () => ac.abort();
  }, [refetch]);

  return { rows, loading, error, refetch };
}
